import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import type { InitialState } from '../types/auth';
import { queryCurrent, queryUsersCraForm, UsersCraFormListItem } from '../services/user';
import { queryMyRoutes, JwtToken } from '../services/users';
import { queryTotalPendingPaymentInstructions } from '../services/instructions';
import { querySolutionApplications, SolutionApplicationItem } from '../services/solution-application';
import { getCardApplicationList, CardApplicationItem } from '../services/card-application';
import { getCardPendingEmbossedList, CardPendingEmbossedListItem } from '../services/card-pending-embossed';
import { queryCurrencies, CurrencyListItem } from '../services/currencies';
import { queryDigitalDepositPending, DigitalDepositPendingListItem, getDigitalDepositStats } from '../services/digital-deposit';
import { getDigitalWithdrawStats } from '../services/digital-withdraw';
import { queryCountries, CountryData } from '../services/country';
import { getScheduledGeneralInstructions } from '../services/schedulers';
import { loginOut } from '../components/RightContent/AvatarDropdown';
import { SessionExpiryWarning } from '../components/SessionExpiryWarning/SessionExpiryWarning';
import defaultSettings from '../../config/defaultSettings';
// New pending count services
import { queryEarnSubscriptionsPendingCount } from '../services/earn';
import { querySecuritiesTransfersPendingCount } from '../services/securities';
import { queryCashInOutPendingCount } from '../services/cash-in-out';
import { queryFundInstructionsPendingCount } from '../services/fund';
import { queryPreciousMetalPendingCount } from '../services/precious-metal';
import type { RequestData } from '@ant-design/pro-table';
import type { MenuDataItem } from '@ant-design/pro-layout';
import { setSupportCenterEnabled } from '../utils/apiErrorReport';

type LinearVendor = {
  id: number;
  name: string;
  defaultUserGroups: string[];
};

interface AuthContextType {
  initialState: InitialState | null;
  setInitialState: React.Dispatch<React.SetStateAction<InitialState | null>>;
  loading: boolean;
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const parseJwt = (token: string): JwtToken => {
  try {
    return JSON.parse(atob(token.split('.')[1]));
  } catch (e) {
    return {
      id: '',
      iat: 0,
      exp: 0
    };
  }
};

const isJwtTokenExpired = (token: string): boolean => {
  const decodedJwt = parseJwt(token);
  // Add 5 second buffer to detect expiry slightly early and avoid in-flight request failures
  return decodedJwt.exp * 1000 < Date.now() + 5000;
};

/**
 * Validate token and clear if invalid
 * Returns true if token is valid
 */
const validateAndClearToken = (): string | null => {
  const token = localStorage.getItem('token');
  if (!token) return null;
  
  if (isJwtTokenExpired(token)) {
    // Token expired - clear stored data
    localStorage.removeItem('token');
    localStorage.removeItem('dashboard-latest-data');
    localStorage.removeItem('dashboard-last-fetch');
    return null;
  }
  
  return token;
};

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [initialState, setInitialState] = useState<InitialState | null>(null);
  const [loading, setLoading] = useState(true);

  // Note: Runtime config is now handled by ConfigProvider
  // which loads before AuthProvider renders

  const fetchUserInfo = useCallback(async () => {
    try {
      const currentUser = await queryCurrent();
      return currentUser;
    } catch (error) {
      // Will be handled by navigation in layout
      return undefined;
    }
  }, []);

  const fetchCraFormInstructions = useCallback(async () => {
    try {
      const craFormInstructions = await queryUsersCraForm({});
      return craFormInstructions;
    } catch (error) {
      return undefined;
    }
  }, []);

  const fetchInstructions = useCallback(async () => {
    try {
      const instructions = await queryTotalPendingPaymentInstructions({ type: 'NON_PAYMENT_INSTRUCTION' });
      return instructions?.total || 0;
    } catch (error) {
      return undefined;
    }
  }, []);

  const fetchPayments = useCallback(async () => {
    try {
      const payments = await queryTotalPendingPaymentInstructions({ type: 'PAYMENT_INSTRUCTION' });
      return payments?.total || 0;
    } catch (error) {
      return undefined;
    }
  }, []);

  const fetchScheduledInstructions = useCallback(async () => {
    try {
      const scheduledInstructions = await getScheduledGeneralInstructions({ active: 'ACTIVE' });
      return scheduledInstructions?.total || 0;
    } catch (error) {
      return undefined;
    }
  }, []);

  const fetchSolutionApplications = useCallback(async () => {
    try {
      const solutionApplications = await querySolutionApplications({ current: 1, pageSize: 20, status: 'PENDING_APPROVAL' });
      return solutionApplications;
    } catch (error) {
      return undefined;
    }
  }, []);

  const fetchCardApplications = useCallback(async () => {
    try {
      const cardApplications = await getCardApplicationList({
        current: 1,
        pageSize: 20,
        status: 'NEW'
      });
      return cardApplications;
    } catch (error) {
      return undefined;
    }
  }, []);

  const fetchCardPendingEmbosses = useCallback(async () => {
    try {
      const pendingEmbossedCards = await getCardPendingEmbossedList({ current: 1, pageSize: 50 });
      return pendingEmbossedCards;
    } catch (error) {
      return undefined;
    }
  }, []);

  const fetchCurrencies = useCallback(async () => {
    try {
      const currencies = await queryCurrencies({});
      return currencies;
    } catch (error) {
      return undefined;
    }
  }, []);

  const fetchCountries = useCallback(async () => {
    try {
      const countries = await queryCountries();
      return countries;
    } catch (error) {
      return undefined;
    }
  }, []);

  const fetchDigitalDepositPending = useCallback(async () => {
    try {
      const digitalDepositPending = await queryDigitalDepositPending({ pageSize: 1, current: 1, pendingCount: true });
      return digitalDepositPending;
    } catch (error) {
      return undefined;
    }
  }, []);

  const fetchDigitalDepositStats = useCallback(async () => {
    try {
      return await getDigitalDepositStats();
    } catch (error) {
      return undefined;
    }
  }, []);

  const fetchDigitalWithdrawStats = useCallback(async () => {
    try {
      return await getDigitalWithdrawStats();
    } catch (error) {
      return undefined;
    }
  }, []);

  const fetchSupportCenterEnabledVendors = useCallback(async () => {
    try {
      const env = (window as any).env;
      const enabledVendors = await fetch(('' + env?.ADMIN_API_URL + `/issues/enabled-vendors`), {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'turnstile-token': localStorage.getItem('turnstileToken') ?? ''
        }
      }).then((res) => res.json());
      return enabledVendors;
    } catch (error) {
      return undefined;
    }
  }, []);

  // New pending count fetch functions
  const fetchEarnSubscriptionsPending = useCallback(async () => {
    try {
      return await queryEarnSubscriptionsPendingCount();
    } catch (error) {
      return undefined;
    }
  }, []);

  const fetchSecuritiesTransfersPending = useCallback(async () => {
    try {
      return await querySecuritiesTransfersPendingCount();
    } catch (error) {
      return undefined;
    }
  }, []);

  const fetchCashInOutPending = useCallback(async () => {
    try {
      return await queryCashInOutPendingCount();
    } catch (error) {
      return undefined;
    }
  }, []);

  const fetchFundInstructionsPending = useCallback(async () => {
    try {
      return await queryFundInstructionsPendingCount();
    } catch (error) {
      return undefined;
    }
  }, []);

  const fetchPreciousMetalPending = useCallback(async () => {
    try {
      return await queryPreciousMetalPendingCount();
    } catch (error) {
      return undefined;
    }
  }, []);

  const fetchMyRoutes = useCallback(async () => {
    try {
      const routes = await queryMyRoutes();
      return routes;
    } catch (error) {
      return [];
    }
  }, []);

  const fetchInitialState = useCallback(async () => {
    try {
      // Config is already loaded by ConfigProvider
      // Validate token immediately before making any API calls
      const token = validateAndClearToken();

      if (!token) {
        // No valid token - set unauthenticated state
        setInitialState({
          fetchUserInfo,
          settings: defaultSettings,
          fetchCraFormInstructions,
          fetchInstructions,
          fetchPayments,
          fetchScheduledInstructions,
          fetchSolutionApplications,
          fetchCardApplications,
          fetchCardPendingEmbosses,
          fetchMyRoutes,
          fetchCurrencies,
          fetchCountries,
          fetchDigitalDepositPending,
          fetchSupportCenterEnabledVendors,
          fetchEarnSubscriptionsPending,
          fetchSecuritiesTransfersPending,
          fetchCashInOutPending,
          fetchFundInstructionsPending,
          fetchPreciousMetalPending
        });
        setLoading(false);
        return;
      }

      // Session expiry warning is now handled by <SessionExpiryWarning /> component
      // which shows a modal with "Stay Logged In" option before logging out

      // Check if MFA enrollment is pending
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        if (payload.mfaPending && !window.location.pathname.startsWith('/mfa-enrollment') && !window.location.pathname.startsWith('/account-settings')) {
          window.location.href = '/mfa-enrollment';
          return;
        }
      } catch {
        // Invalid token format — will be caught by auth guard
      }

      // Clear stale dashboard cache so fetchAndBroadcast fetches fresh counts
      localStorage.removeItem('dashboard-last-fetch');
      localStorage.removeItem('dashboard-latest-data');

      const [currentUser, menuData, supportCenterEnabledVendors] = await Promise.all([
        fetchUserInfo(),
        fetchMyRoutes(),
        fetchSupportCenterEnabledVendors()
      ]);

      setInitialState({
        fetchInstructions,
        fetchPayments,
        fetchScheduledInstructions,
        fetchUserInfo,
        fetchCraFormInstructions,
        fetchMyRoutes,
        fetchSolutionApplications,
        fetchCardApplications,
        fetchCardPendingEmbosses,
        fetchCurrencies,
        fetchCountries,
        fetchDigitalDepositPending,
        fetchDigitalDepositStats,
        fetchDigitalWithdrawStats,
        fetchSupportCenterEnabledVendors,
        fetchEarnSubscriptionsPending,
        fetchSecuritiesTransfersPending,
        fetchCashInOutPending,
        fetchFundInstructionsPending,
        fetchPreciousMetalPending,
        settings: defaultSettings,
        currentUser,
        craFormInstructions: 0,
        instructions: 0,
        payments: 0,
        scheduledInstructions: 0,
        menuData,
        solutionApplications: 0,
        cardApplicationCount: 0,
        cardPendingEmbossedCount: 0,
        currencies: [],
        digitalDepositPending: 0,
        digitalDepositRejected: 0,
        digitalWithdrawPending: 0,
        supportCenterEnabledVendors: supportCenterEnabledVendors || [],
        earnSubscriptionsPending: 0,
        securitiesTransfersPending: 0,
        cashInOutPending: 0,
        fundInstructionsPending: 0,
        preciousMetalPending: 0
      });
      setSupportCenterEnabled(Array.isArray(supportCenterEnabledVendors) && supportCenterEnabledVendors.length > 0);
    } catch (error) {
      console.error('Failed to fetch initial state:', error);

      const httpStatus = (error as any)?.response?.status
        ?? (error as any)?.data?.httpStatus;
      const is401 = httpStatus === 401;

      if (is401) {
        localStorage.removeItem('token');
        localStorage.removeItem('dashboard-latest-data');
        localStorage.removeItem('dashboard-last-fetch');
      }

      setInitialState({
        fetchUserInfo,
        settings: defaultSettings,
        fetchCraFormInstructions,
        fetchInstructions,
        fetchPayments,
        fetchScheduledInstructions,
        fetchSolutionApplications,
        fetchCardApplications,
        fetchCardPendingEmbosses,
        fetchMyRoutes,
        fetchCurrencies,
        fetchCountries,
        fetchDigitalDepositPending,
        fetchDigitalDepositStats,
        fetchDigitalWithdrawStats,
        fetchSupportCenterEnabledVendors,
        fetchEarnSubscriptionsPending,
        fetchSecuritiesTransfersPending,
        fetchCashInOutPending,
        fetchFundInstructionsPending,
        fetchPreciousMetalPending
      });
    } finally {
      setLoading(false);
    }
  }, [
    fetchUserInfo,
    fetchCraFormInstructions,
    fetchInstructions,
    fetchPayments,
    fetchScheduledInstructions,
    fetchSolutionApplications,
    fetchCardApplications,
    fetchCardPendingEmbosses,
    fetchCurrencies,
    fetchCountries,
    fetchDigitalDepositPending,
    fetchDigitalDepositStats,
    fetchDigitalWithdrawStats,
    fetchSupportCenterEnabledVendors,
    fetchMyRoutes,
    fetchEarnSubscriptionsPending,
    fetchSecuritiesTransfersPending,
    fetchCashInOutPending,
    fetchFundInstructionsPending,
    fetchPreciousMetalPending
  ]);

  // Track if we've already fetched to prevent loops
  const hasFetchedRef = useRef(false);

  useEffect(() => {
    // Only fetch once on mount
    if (!hasFetchedRef.current) {
      hasFetchedRef.current = true;
      fetchInitialState();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <AuthContext.Provider value={{ initialState, setInitialState, loading, refresh: fetchInitialState }}>
      {children}
      {initialState?.currentUser && <SessionExpiryWarning />}
    </AuthContext.Provider>
  );
}

// Drop-in replacement for useModel('@@initialState')
export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}

// Alias for easier migration - returns same shape as umi's useModel
export const useModel = (modelName: string) => {
  if (modelName === '@@initialState') {
    const { initialState, setInitialState, loading, refresh } = useAuth();
    return {
      initialState,
      setInitialState,
      loading,
      refresh
    };
  }
  throw new Error(`Unknown model: ${modelName}`);
};

